import React from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import { courseService } from "../../../../../services/course.service";
import { CourseDataInfo } from "../../../../../types";
import { Box, Grid, LinearProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CourseHeaderPreview } from "./componentsPreview/CourseHeaderPreview";
import { CourseLearningObjectivesPreview } from "./componentsPreview/CourseLearningObjectivesPreview";
import { CourseMaterialsPreview } from "./componentsPreview/CourseMaterialsPreview";
import { CourseRequirementsPreview } from "./componentsPreview/CourseRequirementsPreview";
import { CourseDescriptionPreview } from "./componentsPreview/CourseDescriptionPreview";
import { CourseIncludesPreview } from "./componentsPreview/CourseIncludesPreview";
import { CourseRightBlockPreview } from "./componentsPreview/CourseRightBlockPreview";
import Layout from "../../../../../shared/Layout";
import { CourseGetCourses } from "../../../../Panel/components/constructor/ConstructorCourse/ConstructorCourse";

interface CoursePreviewProps {
  course: CourseGetCourses;
  onClose: () => void;
}

const useFetchCourseInfo = (courseId: string) => {
  return useQuery<CourseDataInfo | undefined, Error>({
    queryKey: ["courseInfo", courseId],
    queryFn: () => courseService.getCourseInfo(courseId),
  });
};

const CoursePreview: React.FC<CoursePreviewProps> = ({ onClose, course }) => {
  const {
    data: courseInfo,
    isLoading,
    isError,
    error,
  } = useFetchCourseInfo(course.id || "");

  if (isLoading) {
    return (
      <Box>
        <LinearProgress />
      </Box>
    );
  }
  if (isError) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}>
        <Typography>Ошибка: {error?.message}</Typography>
      </Box>
    );
  }

  if (!courseInfo) {
    return (
      <Layout>
        <Box>Курс не найден</Box>
      </Layout>
    );
  }
  return (
    <Dialog
      fullScreen
      open={true}
      onClose={onClose}
      aria-labelledby="course-preview-title">
      <DialogTitle
        sx={{ display: "flex", justifyContent: "space-between" }}
        id="course-preview-title">
        Предпросмотр курса
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            borderRadius: "8px",
            padding: "10px",
            border: "1px solid",
            color: (theme) => theme.palette.grey[500],
          }}>
          <Typography>Закрыть предпросмотр</Typography> <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          margin: "0 auto",
          padding: "0",
          width: "100%",
        }}>
        <DialogContent>
          <Grid
            container
            spacing="24px"
            sx={{
              "@media (max-width:1000px)": {
                display: "flex",
                flexDirection: "column",
              },
            }}>
            <Grid
              item
              xs={8}
              sx={{
                "@media (max-width:1000px)": {
                  width: "100%",
                  maxWidth: "unset",
                },
              }}>
              <CourseHeaderPreview courseDetails={courseInfo} />
              <CourseLearningObjectivesPreview
                learningObjectives={courseInfo}
              />
              <CourseMaterialsPreview courseMaterials={courseInfo.content} />
              <CourseRequirementsPreview
                courseRequirements={courseInfo.requirements}
              />
              <CourseDescriptionPreview courseDetails={courseInfo} />
            </Grid>

            <Grid
              item
              xs={4}
              sx={{
                "@media (max-width:1000px)": {
                  width: "100%",
                  maxWidth: "unset",
                },
              }}>
              <CourseRightBlockPreview
                courseId={course.id}
                courseDetails={courseInfo}
              />
              <CourseIncludesPreview courseDetails={courseInfo} />
            </Grid>
          </Grid>
        </DialogContent>
      </DialogContent>
    </Dialog>
  );
};

export default CoursePreview;
